import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { formatApiErrorDetail } from "@/lib/api";
import GoogleButton from "@/components/GoogleButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Briefcase, User, ShieldCheck, Star, CheckCircle2, HardHat } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1672748341520-6a839e6c05bb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBjb250cmFjdG9yJTIwcG9ydHJhaXQlMjBzbWlsaW5nfGVufDB8fHx8MTc4NDM2MDAyNnww&ixlib=rb-4.1.0&q=85";

export default function Register() {
  const { register, user, checked } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("business");
  const [loading, setLoading] = useState(false);

  const submittedRef = useRef(false);
  useEffect(() => {
    if (submittedRef.current || !checked || !user) return;
    if (user.role === "business") navigate("/dashboard", { replace: true });
    else if (user.role === "admin") navigate("/admin", { replace: true });
    else navigate("/onboarding", { replace: true });
  }, [checked, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register({ name, email, password, role });
      submittedRef.current = true;
      toast.success("¡Cuenta creada!");
      if (u.role === "business") navigate("/dashboard/business");
      else navigate("/directory");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!checked || (user && !submittedRef.current)) {
    return <div className="flex min-h-screen items-center justify-center bg-background" data-testid="auth-loader"><span className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-primary" /></div>;
  }

  const steps = ["Crea tu cuenta", "Publica tu perfil", "Recibe clientes"];

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
      {/* Left: trust panel */}
      <div className="relative hidden lg:block">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-secondary via-secondary/85 to-secondary/40" />
        <div className="relative flex h-full flex-col justify-between p-12 text-white">
          <Link to="/" className="flex items-center gap-2" data-testid="register-brand-link">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary"><HardHat className="h-5 w-5" /></span>
            <span className="font-head text-xl font-extrabold">{t("brand")}</span>
          </Link>
          <div className="max-w-md">
            <div className="mb-5 flex gap-1">{[...Array(5)].map((_, i) => <Star key={i} className="h-5 w-5 fill-primary text-primary" />)}</div>
            <p className="font-head text-2xl font-bold leading-snug">“URBIS me ayudó a llenar mi agenda de clientes en mi ciudad. Ahora recibo solicitudes cada semana.”</p>
            <p className="mt-4 text-sm text-white/70">— Carlos M., contratista de plomería</p>
          </div>
          <ul className="space-y-3">
            {["Perfil profesional que genera confianza", "Solicitudes y chat de clientes reales", "Pagos protegidos dentro de la plataforma"].map((f) => (
              <li key={f} className="flex items-center gap-2.5 text-sm text-white/90"><CheckCircle2 className="h-5 w-5 text-primary" /> {f}</li>
            ))}
          </ul>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center bg-background px-6 py-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className="mb-6 flex items-center justify-between text-xs font-bold uppercase tracking-wider text-slate-400" data-testid="register-steps">
            {steps.map((s, i) => (
              <div key={s} className="flex items-center gap-2">
                <span className={`flex h-6 w-6 items-center justify-center rounded-full ${i === 0 ? "bg-primary text-white" : "bg-slate-200 text-slate-500"}`}>{i + 1}</span>
                <span className={i === 0 ? "text-secondary" : ""}>{s}</span>
                {i < steps.length - 1 && <span className="mx-1 h-px w-4 bg-slate-200" />}
              </div>
            ))}
          </div>

          <h1 className="font-head text-3xl font-extrabold tracking-tight text-secondary">{t("register_title")}</h1>
          <p className="mt-1.5 text-sm text-slate-500">Únete gratis. Publica tu perfil y empieza a recibir clientes.</p>

          <div className="mt-7"><GoogleButton role={role} /></div>
          <div className="my-5 flex items-center gap-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
            <span className="h-px flex-1 bg-slate-200" /> o <span className="h-px flex-1 bg-slate-200" />
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="register-form">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("account_type")}</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                {[["business", Briefcase, t("business")], ["client", User, t("client")]].map(([val, Icon, label]) => (
                  <button key={val} type="button" onClick={() => setRole(val)} className={`flex items-center justify-center gap-2 rounded-xl border-2 px-3 py-3 text-sm font-semibold transition-colors ${role === val ? "border-primary bg-primary/10 text-primary" : "border-slate-200 text-slate-600 hover:border-slate-300"}`} data-testid={`role-${val}-btn`}>
                    <Icon className="h-4 w-4" /> {label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("name")}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} required className="mt-1.5 rounded-xl" data-testid="register-name-input" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("email")}</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 rounded-xl" data-testid="register-email-input" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("password")}</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="mt-1.5 rounded-xl" data-testid="register-password-input" />
            </div>
            <Button type="submit" disabled={loading} className="h-11 w-full rounded-full bg-primary text-base hover:bg-orange-600" data-testid="register-submit-btn">
              {loading ? "…" : t("register_title")}
            </Button>
          </form>

          <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-slate-400"><ShieldCheck className="h-4 w-4 text-accent" /> Tus datos están protegidos. Sin tarjeta para registrarte.</p>
          <p className="mt-5 text-center text-sm text-slate-500">
            {t("have_account")} <Link to="/login" className="font-semibold text-primary hover:underline" data-testid="go-login-link">{t("nav_login")}</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
